<template>
    <div class="mass-management-container">
        <div class="custom-fields-section">
            <h6 class="mb-3">Campos personalizados</h6>

            <div class="custom-fields-controls compact mb-2 d-flex align-items-center justify-content-start">
                <b-form-checkbox class="select-all-checkbox" size="sm" :checked="allCustomFieldsSelected"
                    @change="onSelectAllChange" aria-label="Seleccionar todos">Todos</b-form-checkbox>
                <div class="text-muted small mr-3">{{ selectedCustomFieldIds.length }} / {{ (customFields || []).length
                }}</div>
                <div class="help-text text-muted small">Elige los campos que quieras incluir en la plantilla.</div>
            </div>

            <div v-if="customFields && customFields.length" class="custom-fields-grid compact">
                <div v-for="(field, idx) in customFields.filter(f => f.activo === 1)" :key="getFieldKey(field) || idx"
                    class="custom-field-card compact" :class="{ 'selected': isFieldSelected(field) }"
                    @click="toggleField(field)" @keydown.enter.prevent="toggleField(field)"
                    @keydown.space.prevent="toggleField(field)" role="button"
                    :aria-pressed="isFieldSelected(field) ? 'true' : 'false'" tabindex="0">
                    <div class="field-chip">
                        <div class="field-title">{{ displayFieldLabel(field) }}</div>
                        <span class="chip-badge" :class="{ 'active': isFieldSelected(field) }" aria-hidden="true">
                            <i class="fa" :class="isFieldSelected(field) ? 'fa-check' : 'fa-plus'"></i>
                        </span>
                    </div>
                </div>
            </div>
            <div v-else class="text-muted">No hay campos personalizados disponibles.</div>
        </div>
        <div class="actions-grid">
            <div class="action-card" :class="{ 'disabled': !hasSelectedProducts }" @click="downloadTemplate">
                <div class="action-icon download-icon">
                    <i class="fa fa-download"></i>
                </div>
                <h6 class="action-title">Descargar plantilla</h6>
                <p class="action-description" style="font-size:11px; line-height:1.2;">
                    Para que la importación funcione correctamente, debes usar exactamente
                    este mismo archivo, sin copiar la información a otro libro ni cambiarle el nombre.
                    Cualquier copia o recreación del archivo hará que no se pueda validar durante la carga.
                </p>
            </div>

            <div class="action-card" :class="{ 'disabled': !canImportExcel }" @click="triggerFileUpload">
                <div class="action-icon upload-icon">
                    <i class="fa fa-upload"></i>
                </div>
                <h6 class="action-title">Importar Excel</h6>
                <p class="action-description" style="font-size:11px; line-height:1.2;">Selecciona el archivo que
                    descargaste y que tiene nueva información. Si
                    cargas una copia o un Excel distinto, el sistema no podrá validarlo.</p>
            </div>
        </div>

        <input ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileUpload" style="display: none;">

        <div v-if="selectedFile" class="file-info-card">
            <div class="file-info-header">
                <div class="file-icon">
                    <i class="fa fa-file-excel-o"></i>
                </div>
                <div class="file-details">
                    <h6 class="file-name">{{ selectedFile.name }}</h6>
                    <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
                <div class="file-actions">
                    <b-button variant="success" size="sm" @click="importExcel" class="import-btn">
                        <i class="fa fa-upload"></i> Importar
                    </b-button>
                    <b-button variant="outline-danger" size="sm" @click="clearFile" class="remove-btn">
                        <i class="fa fa-times"></i>
                    </b-button>
                </div>
            </div>
        </div>

        <div v-if="showValidationResults" class="validation-results mt-3">
            <ValidationIndex :options="validationOptions" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ValidationIndex from '@/views/importValidation/Index.vue';

export default {
    name: "MassManagement",
    components: {
        ValidationIndex
    },
    props: {
        selectedProductsIds: {
            type: Array,
            default: () => []
        },
        selectAllProducts: {
            type: Boolean,
            default: false
        },
        canImportExcel: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            selectedFile: null,
            selectedCustomFieldIds: [],
            showValidationResults: false,
            validationOptions: {
                title: 'Resultados de Importación Masiva',
                validationErrors: [],
                successMessage: 'Importación completada exitosamente.',
                errorMessage: 'Se encontraron errores durante la importación.',
                totalImport: 0,
                textImport: false
            }
        };
    },
    computed: {
        ...mapState("productos", ["customFields"]),

        hasSelectedProducts() {
            return this.selectedProductsIds.length > 0 || this.selectAllProducts;
        },

        allCustomFieldsSelected() {
            return (this.customFields || []).length > 0 && this.selectedCustomFieldIds.length === (this.customFields || []).length;
        },
    },
    watch: {
        customFields: {
            handler(newVal) {
                this.selectedCustomFieldIds = [];
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions('ppam', ['exportMassManagement', 'importMassManagement']),

        async downloadTemplate() {
            if (!this.hasSelectedProducts) {
                this.$alertError("Debe seleccionar al menos un producto para descargar la plantilla.", 3000, false, "¡Advertencia!", "warning");
                return;
            }

            await this.exportMassManagement({
                selectedProductsIds: this.selectedProductsIds,
                selectAllProducts: this.selectAllProducts,
                selectedCustomFields: this.selectedCustomFieldIds
            });
        },

        triggerFileUpload() {
            if (!this.canImportExcel) {
                this.$alertError("No se puede importar Excel ya que tiene un seguimiento.", 3000, false, "¡Advertencia!", "warning");
                return;
            }

            this.selectedFile = null;
            this.$refs.fileInput.value = '';
            this.showValidationResults = false;
            this.validationOptions.validationErrors = [];
            this.validationOptions.totalImport = 0;
            this.validationOptions.textImport = false;
            this.$emit('file-cleared');
            this.$refs.fileInput.click();
        },

        toggleField(field) {
            const k = this.getFieldKey(field);
            if (!k) return;
            const idx = this.selectedCustomFieldIds.indexOf(k);
            if (idx === -1) this.selectedCustomFieldIds.push(k);
            else this.selectedCustomFieldIds.splice(idx, 1);
        },

        isFieldSelected(field) {
            const k = this.getFieldKey(field);
            return k ? this.selectedCustomFieldIds.indexOf(k) !== -1 : false;
        },

        displayFieldLabel(field) {
            return (field && field.nombre) || '';
        },

        getFieldKey(field) {
            if (!field) return null;
                if (field.key != null) return String(field.key);
                if (field.id != null) return String(field.id);
                if (field.nombre != null) return String(field.nombre);
                return null;
        },

        onSelectAllChange(event) {
            const checked = event && event.target ? !!event.target.checked : !!event;
            if (checked) {
                const keys = (this.customFields || []).map(f => this.getFieldKey(f)).filter(Boolean);
                this.selectedCustomFieldIds = Array.from(new Set(keys));
            } else {
                this.selectedCustomFieldIds = [];
            }
        },

        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            const maxSize = 20 * 1024 * 1024;

            if (!allowedTypes.includes(file.type)) {
                this.$alertError('Por favor seleccione un archivo Excel válido (.xlsx o .xls)', 3000, false, '¡Error!', 'error');
                event.target.value = '';
                return;
            }

            if (file.size > maxSize) {
                this.$alertError('El archivo es demasiado grande. Máximo 20MB permitido.', 3000, false, '¡Error!', 'error');
                event.target.value = '';
                return;
            }

            this.selectedFile = new File([file], file.name, {
                type: file.type,
                lastModified: file.lastModified
            });

            this.$emit('file-selected', this.selectedFile);
        },

        clearFile() {
            this.selectedFile = null;
            this.$refs.fileInput.value = '';
            this.showValidationResults = false;
            this.validationOptions.validationErrors = [];
            this.$emit('file-cleared');
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        async importExcel() {
            if (!this.selectedFile) {
                this.$alertError('Por favor seleccione un archivo Excel', 3000, false, '¡Advertencia!', 'warning');
                return;
            }

            this.showValidationResults = false;
            this.validationOptions.validationErrors = [];

            try {
                const fileToSend = new File([this.selectedFile], this.selectedFile.name, {
                    type: this.selectedFile.type,
                    lastModified: Date.now()
                });

                const result = await this.importMassManagement({ file: fileToSend });

                if (result) {
                    this.validationOptions = {
                        ...this.validationOptions,
                        totalImport: result.processed,
                        textImport: true,
                        validationErrors: result.errors
                    };
                    this.showValidationResults = true;
                }

                this.$emit('import-completed', result);
            } catch (error) {
                this.showValidationResults = true;
            }
        },

        cancel() {
            this.$emit('cancel');
        }
    }
}
</script>

<style scoped>
.mass-management-container {
    padding: 18px;
    width: 100%;
    margin: 0;
}

.actions-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
}

.action-card {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 25px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.action-card:hover {
    border-color: #3498db;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(52, 152, 219, 0.15);
}

.action-card:hover .action-icon {
    transform: scale(1.1);
}

.action-card.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #e9ecef;
}

.action-card.disabled:hover {
    border-color: #e9ecef;
    transform: none;
    box-shadow: none;
}

.action-card.disabled:hover .action-icon {
    transform: none;
}

.action-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 15px;
    transition: all 0.3s ease;
    font-size: 20px;
    color: white;
}

.download-icon {
    background: linear-gradient(135deg, #3498db, #2980b9);
}

.upload-icon {
    background: linear-gradient(135deg, #27ae60, #229954);
}

.action-title {
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 16px;
}

.action-description {
    color: #7f8c8d;
    font-size: 12px;
    margin-bottom: 0;
    line-height: 1.4;
}

.file-info-card {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border: 2px solid #dee2e6;
    border-radius: 12px;
    padding: 20px;
    margin-top: 20px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
}

.file-info-header {
    display: flex;
    align-items: center;
    gap: 15px;
}

.file-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.file-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #27ae60, #229954);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 18px;
    color: white;
}

.file-details {
    flex: 1;
    min-width: 0;
}

.file-name {
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 14px;
    word-break: break-all;
}

.file-size {
    color: #7f8c8d;
    font-size: 12px;
}

.remove-btn {
    flex-shrink: 0;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.import-btn {
    flex-shrink: 0;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.import-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.validation-results {
    margin-top: 20px;
    width: 100%;
}

.custom-fields-section {
    margin-bottom: 18px;
}

.custom-field-item {
    display: none
}

.custom-fields-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.custom-field-card {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #eef3fb;
    border-radius: 20px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.12s ease-in-out;
    padding: 6px 10px;
    font-size: 12px;
    line-height: 1;
    min-width: 64px;
    max-width: 240px;
}

.custom-field-card.compact {
    padding: 4px 8px;
    font-size: 12px
}

.custom-field-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(33, 37, 41, 0.04);
}

.custom-field-card.selected {
    background: #e9f3ff;
    border-color: #87b9ff;
}

.field-title {
    font-weight: 600;
    color: #2c3e50;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px
}

.chip-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: transparent;
    border: 1px solid #cfe6ff;
    color: #4b6b9a;
    font-size: 11px
}

.chip-badge.active {
    background: #1f6feb;
    border-color: #1f6feb;
    color: #fff
}

.custom-fields-controls.compact {
    gap: 8px;
    padding: 0
}

.select-all-checkbox .form-check-input {
    width: 18px;
    height: 18px
}

.help-text {
    margin-left: 12px
}

.custom-field-card:focus {
    outline: 3px solid rgba(43, 138, 239, 0.16);
    outline-offset: 2px
}

.custom-field-card {
    transition: transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease
}

.custom-field-card .field-title {
    text-transform: uppercase;
    letter-spacing: .4px
}

.field-chip {
    display: flex;
    align-items: center;
    gap: 8px
}

.validation-results>>>.import-validation-container {
    max-width: none;
    width: 100%;
}

@media (max-width: 576px) {
    .actions-grid {
        grid-template-columns: 1fr;
        gap: 15px;
    }

    .mass-management-container {
        padding: 20px;
    }
}
</style>
